import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthUser } from '../auth/store/auth.selectors';
import { logout } from '../auth/store/auth.slice';
import { NavLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import logo from './../../assets/images/logo.png';
import { Home, Menu as MenuIcon, MapOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AddRoadOutlinedIcon from '@mui/icons-material/AddRoadOutlined';
import PropTypes from 'prop-types';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  padding: '12px 32px',
  borderBottom: 'none',
  height: 'auto',
  position: 'relative',
  backdropFilter: 'blur(10px)',
  [theme.breakpoints.down('md')]: {
    padding: '12px 16px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)',
  },
}));

const Nav = styled(NavLink)(({ theme }) => ({
  color: '#667eea',
  textDecoration: 'none',
  fontSize: '0.95rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    color: '#6671eea',
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    transform: 'translateY(-2px)',
  },
  '&.active': {
    color: '#667eea',
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
  },
}));

const NavLinkContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '48px',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const UserProfile = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '8px 16px',
  borderRadius: '12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: 'rgba(102, 126, 234, 0.12)',
  border: '1px solid rgba(102, 126, 234, 0.12)',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.2)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '12px',
    minWidth: '240px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(12px)',
    animation: 'fadeIn 0.2s ease-in-out',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.95rem',
  padding: '10px 16px',
  margin: '4px 8px',
  borderRadius: '8px',
  transition: 'all 0.2s ease-in-out',
  color: '#2C2C2C',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    color: '#667eea',
    paddingLeft: '20px',
  },
}));

const LogoutMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.95rem',
  padding: '10px 16px',
  margin: '4px 8px',
  borderRadius: '8px',
  color: '#DC3545',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    color: '#C82333',
    paddingLeft: '20px',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: '#2C2C2C',
  borderRadius: '10px',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    color: '#667eea',
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));

const CenteredTypography = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '1.4rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  justifyContent: 'flex-end',
  minHeight: '64px',
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
}));

const Header = ({ setOpenPwdModal, setOpenMMModal }) => {
  const user = useSelector(selectAuthUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event, reason) => {
    setAnchorEl(null);
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    else {
      dispatch(logout());
      navigate('/login');
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleLogout = () => {
    setDrawerOpen(false);
    dispatch(logout());
    navigate('/login');
  };

  const handleNavigation = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <StyledAppBar position="static">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={logo}
            alt="Rieker"
            style={{
              height: isMobile ? '32px' : '40px',
              marginRight: isMobile ? '0' : '24px',
            }}
          />

          <NavLinkContainer>
            <Nav to="/">
              <Home sx={{ fontSize: 20, color: '#667eea' }} />
              Home
            </Nav>
            <Nav to="/god">
              <MapOutlined sx={{ fontSize: 20, color: '#667eea' }} />
              God View
            </Nav>
          </NavLinkContainer>
        </Box>

        <CenteredTypography variant="h6" fontWeight="bold">
          SIGNS
        </CenteredTypography>

        <UserProfile
          onClick={handleClick}
          aria-controls={open ? 'profile-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar
              alt="User"
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              }}
            >
              {user?.name.length ? user.name.charAt(0) : 'U'}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#2C2C2C',
                  lineHeight: '1.2',
                }}
              >
                {user?.name.length ? user.name : ''}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: '#666666',
                  lineHeight: '1.2',
                }}
              >
                {user?.rolename}
              </Typography>
            </Box>
          </Box>
        </UserProfile>

        {/* Mobile Menu Button */}
        <MobileMenuButton color="inherit" aria-label="menu" onClick={toggleDrawer(true)} edge="end">
          <MenuIcon />
        </MobileMenuButton>

        {/* Dropdown Menu */}
        <StyledMenu
          id="profile-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'profile-button',
          }}
        >
          <StyledMenuItem
            onClick={() => {
              setAnchorEl(null);
              setOpenPwdModal(true);
            }}
          >
            <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
              <PasswordOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </StyledMenuItem>
          <Divider sx={{ my: 0.5 }} />
          <StyledMenuItem
            onClick={() => {
              setAnchorEl(null);
              setOpenMMModal(true);
            }}
          >
            <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
              <AddRoadOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Mile Markers</ListItemText>
          </StyledMenuItem>
          <Divider sx={{ my: 0.5 }} />
          <LogoutMenuItem onClick={handleClose}>
            <ListItemIcon sx={{ color: '#DC3545', minWidth: 40 }}>
              <LogoutOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </LogoutMenuItem>
        </StyledMenu>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#FAFAFA',
            },
          }}
        >
          <Box sx={{ width: 280 }} role="presentation">
            <DrawerHeader>
              <IconButton
                onClick={toggleDrawer(false)}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#667eea',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DrawerHeader>
            <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
            {/* User info in drawer */}
            {user && (
              <>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: '12px',
                    m: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      mr: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    {user?.name?.length ? user.name.charAt(0) : 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {user?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.rolename}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
              </>
            )}

            <List>
              <ListItem
                button
                onClick={() => handleNavigation('/')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
                  <Home />
                </ListItemIcon>
                <ListItemText primary="Company" />
              </ListItem>
              {/* <ListItem
                button
                onClick={() => handleNavigation('/users')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
                  <Person />
                </ListItemIcon>
                <ListItemText primary="Users" />
              </ListItem> */}
            </List>
            <Divider sx={{ borderColor: 'rgba(102, 126, 234, 0.08)' }} />
            <List>
              <ListItem
                button
                onClick={() => {
                  setAnchorEl(null);
                  setOpenPwdModal(true);
                }}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
                  <PasswordOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Change Password" />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  setAnchorEl(null);
                  setOpenMMModal(true);
                }}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
                  <AddRoadOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Mile Markers" />
              </ListItem>
              <ListItem
                button
                onClick={handleLogout}
                sx={{
                  mx: 1,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#DC3545', minWidth: 40 }}>
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" sx={{ color: '#DC3545' }} />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </Box>
    </StyledAppBar>
  );
};

export default Header;

Header.propTypes = {
  setOpenPwdModal: PropTypes.func.isRequired,
  setOpenMMModal: PropTypes.func.isRequired,
};
